// @popcart/core/v1: the typed client for /api/v1, shared by the customer app, rider app and back
// office. Shapes: ./contract.d.ts. Hermes-safe: no URLSearchParams with objects, no Intl.

export { planDeliveries } from './plan.js';
export * from './catchweight.js';
export * from './grill.js';
export * from './mart.js';
export * from './express.js';
export * from './money.js';
export * from './shopSlug.js';
export * from './geohash.js';
export * from './pickupCode.js';
export * from './semver.js';

export class ApiRequestError extends Error {
  /** @param {number} status @param {import('./contract').ApiError} body */
  constructor(status, body) {
    super(body?.error || `Request failed (${status})`);
    this.status = status;
    this.code = body?.code || 'internal';
    this.field = body?.field;
    this.details = body?.details;
  }
}

const qs = (params) => {
  const parts = Object.entries(params || {})
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

/** A random idempotency key without crypto.randomUUID (absent in React Native). */
export function newIdempotencyKey() {
  let s = '';
  for (let i = 0; i < 32; i++) s += Math.floor(Math.random() * 16).toString(16);
  return s;
}

/**
 * v5 B (the rider app's offline queue): a stop action's Idempotency-Key and the time the phone
 * says it happened. No opts → no headers (the old call).
 * @param {{ idempotencyKey?: string; occurredAt?: string } | undefined} opts
 */
function actionHeaders(opts) {
  const h = {};
  if (opts?.idempotencyKey) h['Idempotency-Key'] = opts.idempotencyKey;
  if (opts?.occurredAt) h['X-Occurred-At'] = opts.occurredAt;
  return h;
}

/**
 * @param {{ baseUrl: string; getToken?: () => string | null | Promise<string | null>; onUnauthorized?: () => void; fetchImpl?: typeof fetch }} opts
 */
export function createApi({ baseUrl, getToken = () => null, onUnauthorized, fetchImpl }) {
  const doFetch = fetchImpl || ((...a) => fetch(...a));
  const root = baseUrl.replace(/\/+$/, '') + '/api/v1';

  async function call(method, path, { body, headers, form } = {}) {
    const token = await getToken();
    const h = { Accept: 'application/json', ...(headers || {}) };
    if (token) h.Authorization = `Bearer ${token}`;
    let payload;
    if (form) payload = form;
    else if (body !== undefined) {
      h['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }
    let res;
    try {
      res = await doFetch(root + path, { method, headers: h, body: payload });
    } catch (e) {
      // Dev builds say why (a refused upload body looks exactly like being offline otherwise).
      if (typeof __DEV__ !== 'undefined' && __DEV__) console.warn(`[api] ${method} ${path} never reached the server:`, e?.message || e);
      throw new ApiRequestError(0, { error: "Can't reach PopCart. Check your connection.", code: 'network' });
    }
    if (res.status === 204 || res.status === 202 && res.headers.get('content-length') === '0') return null;
    let data = null;
    try {
      data = await res.json();
    } catch {
      data = null;
    }
    if (!res.ok) {
      if (res.status === 401 && onUnauthorized) onUnauthorized();
      throw new ApiRequestError(res.status, data);
    }
    return data;
  }

  return {
    // auth
    signInWithGoogle: (idToken) => call('POST', '/auth/google', { body: { idToken } }),
    signInWithApple: (req) => call('POST', '/auth/apple', { body: req }),
    startEmailSignIn: (email) => call('POST', '/auth/email/start', { body: { email } }),
    verifyEmailCode: (email, code) => call('POST', '/auth/email/verify', { body: { email, code } }),
    signOut: () => call('POST', '/auth/logout'),
    // me
    me: () => call('GET', '/me'),
    updateMe: (patch) => call('PATCH', '/me', { body: patch }),
    uploadAvatar: (form) => call('PUT', '/me/avatar', { form }),
    removeAvatar: () => call('DELETE', '/me/avatar'),
    config: () => call('GET', '/config'),
    deleteAccount: () => call('DELETE', '/me', { body: { confirm: 'DELETE' } }),
    // addresses
    addresses: () => call('GET', '/addresses'),
    addAddress: (input) => call('POST', '/addresses', { body: input }),
    updateAddress: (id, patch) => call('PATCH', `/addresses/${id}`, { body: patch }),
    useVerifiedSpot: (id) => call('POST', `/addresses/${id}/use-verified-spot`),
    removeAddress: (id) => call('DELETE', `/addresses/${id}`),
    checkServiceArea: (latLng) => call('POST', '/service-area/check', { body: latLng }),
    // catalogue
    home: (addressId) => call('GET', `/home${qs({ addressId })}`),
    shops: (params) => call('GET', `/shops${qs(params)}`),
    products: (params) => call('GET', `/products${qs(params)}`),
    product: (id) => call('GET', `/products/${id}`),
    search: (q) => call('GET', `/search${qs({ q })}`),
    // quote & orders
    quote: (req) => call('POST', '/quote', { body: req }),
    placeOrder: (req, idempotencyKey) => call('POST', '/orders', { body: req, headers: { 'Idempotency-Key': idempotencyKey } }),
    orders: (cursor) => call('GET', `/orders${qs({ cursor })}`),
    order: (id) => call('GET', `/orders/${id}`),
    cancelOrder: (id, reason) => call('POST', `/orders/${id}/cancel`, { body: { reason } }),
    reorder: (id) => call('POST', `/orders/${id}/reorder`),
    rateOrder: (id, stars, comment) => call('POST', `/orders/${id}/rating`, { body: { stars, comment } }),
    registerPushToken: (token, platform, app) => call('POST', '/push-tokens', { body: app ? { token, platform, app } : { token, platform } }),
    // shop (Partner Hub)
    shopToday: (shopId) => call('GET', `/shop/${shopId}/today`),
    shopQueue: (shopId, status) => call('GET', `/shop/${shopId}/queue${qs({ status })}`),
    advanceShopOrder: (shopId, shopOrderId, to) => call('POST', `/shop/${shopId}/orders/${shopOrderId}/advance`, { body: { to } }),
    cancelShopOrder: (shopId, shopOrderId, reason) => call('POST', `/shop/${shopId}/orders/${shopOrderId}/cancel`, { body: { reason } }),
    setAcceptingOrders: (shopId, acceptingOrders) => call('PATCH', `/shop/${shopId}`, { body: { acceptingOrders } }),
    shopStock: (shopId, low) => call('GET', `/shop/${shopId}/stock${qs({ low: low ? 1 : undefined })}`),
    adjustStock: (shopId, productId, delta, reason, note) => call('POST', `/shop/${shopId}/stock/${productId}`, { body: { delta, reason, note } }),
    // Phase 3 · Fresh packing station + arrivals
    weighQueue: (shopId) => call('GET', `/shop/${shopId}/weigh-queue`),
    weighItem: (shopId, orderItemId, form) => call('POST', `/shop/${shopId}/items/${orderItemId}/weigh`, { form }),
    arrivals: (shopId, since) => call('GET', `/shop/${shopId}/arrivals${qs({ since })}`),
    logArrival: (shopId, productId, quantity, source) => call('POST', `/shop/${shopId}/arrivals`, { body: { productId, quantity, source } }),
    setupFreshProduct: (shopId, productId, setup) => call('PUT', `/shop/${shopId}/products/${productId}/fresh`, { body: setup }),
    // Catalogue management (Partner Hub + Ops)
    adminProducts: (shopId, params) => call('GET', `/shop/${shopId}/products${qs(params)}`),
    adminProduct: (shopId, productId) => call('GET', `/shop/${shopId}/products/${productId}`),
    createProduct: (shopId, input) => call('POST', `/shop/${shopId}/products`, { body: input }),
    updateProduct: (shopId, productId, patch) => call('PATCH', `/shop/${shopId}/products/${productId}`, { body: patch }),
    setProductVisible: (shopId, productId, visible) => call('POST', `/shop/${shopId}/products/${productId}/visibility`, { body: { visible } }),
    deleteProduct: (shopId, productId) => call('DELETE', `/shop/${shopId}/products/${productId}`),
    restoreProduct: (shopId, productId) => call('POST', `/shop/${shopId}/products/${productId}/restore`),
    uploadProductImage: (shopId, productId, form) => call('POST', `/shop/${shopId}/products/${productId}/image`, { form }),
    productAudit: (shopId, productId) => call('GET', `/shop/${shopId}/products/${productId}/audit`),
    categories: (line) => call('GET', `/categories${qs({ line })}`),
    opsProducts: (params) => call('GET', `/ops/products${qs(params)}`),
    opsCategories: () => call('GET', '/ops/categories'),
    createCategory: (input) => call('POST', '/ops/categories', { body: input }),
    updateCategory: (id, patch) => call('PATCH', `/ops/categories/${id}`, { body: patch }),
    deleteCategory: (id) => call('DELETE', `/ops/categories/${id}`),
    mapHealth: () => call('GET', '/maps/health'),
    orderRoute: (orderId) => call('GET', `/orders/${orderId}/route`),
    riderJobRoute: (deliveryId) => call('GET', `/rider/jobs/${deliveryId}/route`),
    // Phase 7 · Ops console, Partner Hub back office, rider earnings
    opsCustomers: (params) => call('GET', `/ops/customers${qs(params)}`),
    opsCustomer: (userId) => call('GET', `/ops/customers/${userId}`),
    opsPayments: () => call('GET', '/ops/payments'),
    addRemittance: (riderUserId, input) => call('POST', `/ops/riders/${riderUserId}/remittances`, { body: input }),
    opsCatchWeight: (params) => call('GET', `/ops/catch-weight${qs(params)}`),
    opsRefunds: (params) => call('GET', `/ops/refunds${qs(params)}`),
    processRefund: (adjustmentId, input) => call('POST', `/ops/refunds/${adjustmentId}`, { body: input }),
    createRefund: (orderId, input) => call('POST', `/ops/orders/${orderId}/refunds`, { body: input }),
    opsPayouts: (params) => call('GET', `/ops/payouts${qs(params)}`),
    createPayout: (input) => call('POST', '/ops/payouts', { body: input }),
    markPayoutPaid: (payoutId, reference) => call('POST', `/ops/payouts/${payoutId}/paid`, { body: { reference } }),
    voidPayout: (payoutId) => call('POST', `/ops/payouts/${payoutId}/void`),
    opsZones: () => call('GET', '/ops/zones'),
    createZone: (input) => call('POST', '/ops/zones', { body: input }),
    updateZone: (zoneId, input) => call('PUT', `/ops/zones/${zoneId}`, { body: input }),
    deleteZone: (zoneId) => call('DELETE', `/ops/zones/${zoneId}`),
    opsReport: (params) => call('GET', `/ops/reports${qs(params)}`),
    opsReviewQueue: (status) => call('GET', `/ops/catalogue/review${qs({ status })}`),
    reviewProduct: (productId, input) => call('POST', `/ops/catalogue/review/${productId}`, { body: input }),
    shopHours: (shopId) => call('GET', `/shop/${shopId}/hours`),
    updateShopHours: (shopId, input) => call('PUT', `/shop/${shopId}/hours`, { body: input }),
    shopStaff: (shopId) => call('GET', `/shop/${shopId}/staff`),
    addShopStaff: (shopId, input) => call('POST', `/shop/${shopId}/staff`, { body: input }),
    removeShopStaff: (shopId, userId) => call('DELETE', `/shop/${shopId}/staff/${userId}`),
    shopReviews: (shopId, cursor) => call('GET', `/shop/${shopId}/reviews${qs({ cursor })}`),
    replyReview: (shopId, ratingId, text) => call('POST', `/shop/${shopId}/reviews/${ratingId}/reply`, { body: { text } }),
    shopSales: (shopId, params) => call('GET', `/shop/${shopId}/sales${qs(params)}`),
    shopHandoff: (shopId) => call('GET', `/shop/${shopId}/handoff`),
    // v5 D · the shop's PC confirms the rider's offline pickup code
    confirmPickup: (shopId, shopOrderId, code) => call('POST', `/shop/${shopId}/handoff/${shopOrderId}/confirm`, { body: { code } }),
    riderEarnings: (days) => call('GET', `/rider/earnings${qs({ days })}`),
    // Phase 6 · Express
    myCredits: () => call('GET', '/me/credits'),
    opsExpress: () => call('GET', '/ops/express'),
    issueCredit: (input) => call('POST', '/ops/express/credits', { body: input }),
    // Phase 5 · Mart
    pickList: (shopId) => call('GET', `/shop/${shopId}/pick-list`),
    pickItem: (shopId, orderItemId, input) => call('POST', `/shop/${shopId}/items/${orderItemId}/pick`, { body: input }),
    substitutes: (shopId, productId, qty) => call('GET', `/shop/${shopId}/substitutes${qs({ productId, qty })}`),
    barcodeLookup: (shopId, code) => call('GET', `/shop/${shopId}/barcode/${encodeURIComponent(code)}`),
    // Phase 4 · Grill
    slots: (shopIds, addressId) => call('GET', `/slots${qs({ shopIds: shopIds.join(','), addressId })}`),
    kitchen: (shopId) => call('GET', `/shop/${shopId}/kitchen`),
    moveTicket: (shopId, shopOrderId, to) => call('POST', `/shop/${shopId}/kitchen/${shopOrderId}`, { body: { to } }),
    shopMenu: (shopId) => call('GET', `/shop/${shopId}/menu`),
    setModifierOption: (shopId, optionId, isAvailable) => call('PATCH', `/shop/${shopId}/modifier-options/${optionId}`, { body: { isAvailable } }),
    setupGrillProduct: (shopId, productId, setup) => call('PUT', `/shop/${shopId}/products/${productId}/grill`, { body: setup }),
    // ops (super-admin)
    opsOverview: (range = 'today') => call('GET', `/ops/overview${qs({ range })}`),
    opsBoard: () => call('GET', '/ops/board'),
    opsShops: () => call('GET', '/ops/shops'),
    setShopStatus: (shopId, status) => call('POST', `/ops/shops/${shopId}/status`, { body: { status } }),
    opsRiders: () => call('GET', '/ops/riders'),
    opsRiderProfile: (userId) => call('GET', `/ops/riders/${userId}/profile`),
    opsTeam: (role) => call('GET', `/ops/team${qs({ role })}`),
    inviteTeamMember: (body) => call('POST', '/ops/team/invite', { body }),
    revokeTeamRole: (userId, role) => call('DELETE', `/ops/team/${userId}/roles/${role}`),
    opsAudit: (params) => call('GET', `/ops/audit${qs(params || {})}`),
    opsShop: (shopId) => call('GET', `/ops/shops/${shopId}`),
    shopRef: (ref) => call('GET', `/shop-ref/${encodeURIComponent(ref)}`),
    createShop: (body) => call('POST', '/ops/shops', { body }),
    updateShop: (shopId, patch) => call('PATCH', `/ops/shops/${shopId}`, { body: patch }),
    verifyShop: (shopId) => call('POST', `/ops/shops/${shopId}/verify`, { body: {} }),
    inviteShopOwner: (shopId, email) => call('POST', `/ops/shops/${shopId}/owners`, { body: { email } }),
    assignDelivery: (deliveryId, riderUserId) => call('POST', `/ops/deliveries/${deliveryId}/assign`, { body: { riderUserId } }),
    opsSettings: () => call('GET', '/ops/settings'),
    putOpsSetting: (key, value) => call('PUT', `/ops/settings/${key}`, { body: value }),
    // rider
    riderOnline: (online) => call('POST', '/rider/online', { body: { online } }),
    riderJobs: () => call('GET', '/rider/jobs'),
    riderProfile: () => call('GET', '/rider/profile'),
    updateRiderProfile: (patch) => call('PATCH', '/rider/profile', { body: patch }),
    acceptJob: (deliveryId) => call('POST', `/rider/jobs/${deliveryId}/accept`),
    arriveStop: (deliveryId, seq, opts) => call('POST', `/rider/jobs/${deliveryId}/stops/${seq}/arrive`, { headers: actionHeaders(opts) }),
    completeStop: (deliveryId, seq, form, opts) => call('POST', `/rider/jobs/${deliveryId}/stops/${seq}/complete`, { form, headers: actionHeaders(opts) }),
    // v5 C: closed cell-hours of the app's own request outcomes (≤ 200 per call)
    riderNetlog: (buckets) => call('POST', '/rider/netlog', { body: { buckets } }),
    opsCoverage: (days) => call('GET', `/ops/coverage${qs({ days })}`),
    riderPosition: (pos) => call('POST', '/rider/position', { body: pos }),
  };
}
